import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, ComboBox, DatePicker, EMPHASIS, Label, Text, TextField, ToggleGroup} from '@forecast-it/design-system';
import Util from '../../../../shared/util/util';
import CreateTaskSetMutation from '../../../../../mutations/create_task_set_mutation';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {formatSetDate} from './describeTaskSet';
import {MAX_OCCURRENCES, TaskSetFormState, useTaskSetPreview} from './useTaskSetPreview';
import {TaskSetDrawer} from './TaskSetDrawer';

const WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const Row = styled.div`
	display: flex;
	gap: 12px;

	> * {
		flex: 1;
		min-width: 0;
	}
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/** Reads as a link, not a button: it reveals fields, it does not act. */
const MoreFieldsToggle = styled.button`
	align-self: flex-start;
	border: 0;
	background: transparent;
	padding: 0;
	display: flex;
	align-items: baseline;
	gap: 8px;
	font-size: 14px;
	font-weight: 500;
	letter-spacing: 0.15px;
	/* The drawer's accent is the design system's blue. The purple belongs to the
	   legacy scoping chrome outside it. */
	color: #0004db;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #0004db;
		outline-offset: 2px;
	}
`;

/** Names what is behind the toggle, so opening it is not a guess. */
const MoreFieldsHint = styled.span`
	font-size: 12px;
	font-weight: 350;
	color: #acacb9;
`;

/**
 * 11px for the short field notes. The estimate's note is 12px in the design, so
 * it opts up rather than every hint growing to match one of them.
 */
const Hint = styled.div<{larger?: boolean}>`
	font-size: ${({larger}) => (larger ? '12px' : '11px')};
	font-weight: 350;
	color: #acacb9;
	line-height: ${({larger}) => (larger ? '17px' : '15px')};
`;

const Callout = styled.div`
	border: 1px solid #e6e6e6;
	background: #fafafb;
	border-radius: 5px;
	padding: 10px 12px;
	font-size: 12px;
	font-weight: 350;
	line-height: 17px;
	color: #4a4e56;
`;

const Preview = styled.div`
	border: 1px solid #e6e6e6;
	background: #fafafb;
	border-radius: 5px;
	padding: 11px 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PreviewStats = styled.div`
	display: flex;
	gap: 26px;
	border-top: 1px solid #e6e6e6;
	padding-top: 9px;
`;

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;

	span {
		font-size: 9px;
		font-weight: 500;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: #acacb9;
	}

	b {
		/* Monospace, per the design: these two numbers sit above each other and are
		   read by comparing them, so the digits have to line up. */
		font-family: 'DM Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace;
		font-size: 15px;
		font-weight: 500;
		font-variant-numeric: tabular-nums;
	}
`;

const DEFAULT_FORM: TaskSetFormState = {
	frequency: 'WEEKLY',
	intervalLength: 1,
	weekdays: ['MONDAY'],
	monthlyWeekday: null,
	monthlyMode: null,
	dayOfMonth: null,
	weekOrdinal: null,
	seedDate: '',
	boundType: 'COUNT',
	boundCount: 12,
	boundEndDate: null,
};

/**
 * Cleared as well as seeded on every switch. Leaving weekdays populated when the
 * user moves to MONTHLY would send a stale weekly day to the backend, and leaving
 * monthlyWeekday null would have the preview compute against no day at all.
 */
/**
 * The day a stored ISO date falls on, or 1 when there is no usable date yet.
 *
 * A monthly rule has to fall on the day the user picked. Defaulting to the 1st
 * silently moved a set seeded 15 June onto 1 July, 1 August -- dates the user
 * never chose and nothing in the form explained.
 */
const dayOfMonthFrom = (iso: string): number => {
	const day = Number((iso || '').slice(8, 10));
	return Number.isInteger(day) && day >= 1 && day <= 31 ? day : 1;
};

/**
 * The weekday a stored ISO date falls on, for the monthly weekday modes.
 * Parsed as UTC so the day does not shift west of UTC.
 */
const weekdayFrom = (iso: string): string => {
	const parsed = new Date(`${iso}T00:00:00Z`);
	if (isNaN(parsed.getTime())) {
		return 'MONDAY';
	}
	return ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][parsed.getUTCDay()];
};

/**
 * What each frequency needs cleared and seeded on a switch.
 *
 * Derived from the current seed date rather than constant, so switching to
 * MONTHLY keeps the day the user already chose. Cleared as well as seeded:
 * leaving weekdays populated when the user moves to MONTHLY would send a stale
 * weekly day, and leaving monthlyWeekday null would have the preview compute
 * against no day at all.
 */
const patternDefaults = (
	frequency: TaskSetFormState['frequency'],
	seedDate: string,
	currentWeekdays: ReadonlyArray<string> = []
): Partial<TaskSetFormState> => {
	const cleared = {weekdays: [], monthlyWeekday: null, monthlyMode: null, dayOfMonth: null, weekOrdinal: null};
	switch (frequency) {
		case 'WEEKLY':
			// Seeded from the date only when nothing is selected. The monthly day
			// below is derived and has to follow the date, but a weekly day set is
			// something the user picked: re-seeding it whenever the date moved
			// collapsed a Mon/Wed/Fri selection to a single day. A frequency change
			// clears the selection first, so switching back to weekly still seeds.
			return {
				...cleared,
				weekdays: currentWeekdays.length > 0 ? [...currentWeekdays] : [weekdayFrom(seedDate)],
			};
		case 'MONTHLY':
			return {
				...cleared,
				monthlyMode: 'DAY_OF_MONTH',
				dayOfMonth: dayOfMonthFrom(seedDate),
				monthlyWeekday: weekdayFrom(seedDate),
				weekOrdinal: 1,
			};
		default:
			return cleared;
	}
};

/**
 * A whole, non-negative occurrence count, or the value already in the form when
 * the keystroke does not make one.
 *
 * Number('12x') is NaN, and NaN went back into the field as the literal string
 * "NaN" with the preview at zero instances and Create disabled with nothing
 * explaining why. A fractional count is refused the same way: a set produces a
 * whole number of tasks.
 */
const countFrom = (entered: string, current: number | null): number | null => {
	if (!entered.trim()) {
		return null;
	}
	const parsed = Number(entered);
	return Number.isInteger(parsed) && parsed >= 0 ? parsed : current;
};

const defaultSetName = (instanceName: string, prefix: string): string => (instanceName ? `${prefix} ${instanceName}` : '');

export interface CreateTaskSetDrawerProps {
	projectId: string;
	/**
	 * Comes from whichever phase group's link was clicked. There is no phase field
	 * in this drawer because the group implies it. Null means the No Phase group,
	 * which is a supported case and not a missing value.
	 */
	phaseId?: string | null;
	projectName?: string;
	phaseName?: string | null;
	isOpen: boolean;
	/** Points projects take the number as-is; hours projects convert to minutes. */
	isPointsProject?: boolean;
	/**
	 * The optional attributes every instance is stamped with. All four already sit
	 * on the scoping page's fragment, so this drawer widens no query.
	 */
	roles?: ReadonlyArray<{id: string; name?: string | null}>;
	assignablePersons?: ReadonlyArray<{id: string; firstName?: string | null; lastName?: string | null}>;
	deliverables?: ReadonlyArray<{id: string; name?: string | null}>;
	statusColumns?: ReadonlyArray<{id: string; name?: string | null}>;
	/**
	 * The hourly rate for a role on a given date, or null when the rate card
	 * prices neither the role nor a default on that date.
	 *
	 * A function of the date, not a flat rate table, because a rate card is
	 * versioned: each row carries a startDate and a role may have several. A set
	 * spanning a rate change therefore bills each instance at the rate in force
	 * on that instance's own date. The caller owns resolving that -- see
	 * RateCardUtil.getRoleRateOrDefaultOnDate, which also handles the card's
	 * default for a role it does not price.
	 */
	rateForRoleOnDate?: (roleId: string, date: string) => number | null;
	currencySymbol?: string;
	onClose: () => void;
	onCreated?: () => void;
}

/**
 * A single-select that can be cleared, for the attributes every instance is
 * stamped with. All four are optional, so each carries an explicit "none" entry
 * rather than relying on an empty value the ComboBox cannot represent.
 */
const OptionalPicker = ({
	label,
	noneLabel,
	items,
	value,
	onChange,
	dataCy,
}: {
	label: string;
	noneLabel: string;
	items: ReadonlyArray<{id: string; name: string}>;
	value: string | null;
	onChange: (id: string | null) => void;
	dataCy: string;
}) => {
	const NONE = '__none__';
	return (
		<Field>
			<Label>{label}</Label>
			<ComboBox
				aria-label={label}
				data-cy={dataCy}
				defaultItems={[{id: NONE, name: noneLabel}, ...items.map(i => ({id: i.id, name: i.name}))]}
				value={value ?? NONE}
				onChange={(next: string) => onChange(next === NONE ? null : next)}
				width="flex"
			>
				{item => (
					<ComboBox.Item textValue={item.name}>
						<Text noWrap>{item.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
		</Field>
	);
};

export const CreateTaskSetDrawer = ({
	projectId,
	phaseId,
	projectName,
	phaseName,
	isOpen,
	isPointsProject = false,
	roles = [],
	assignablePersons = [],
	deliverables = [],
	statusColumns = [],
	rateForRoleOnDate,
	currencySymbol = '',
	onClose,
	onCreated,
}: CreateTaskSetDrawerProps) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const [instanceName, setInstanceName] = useState('');
	const [setName, setSetName] = useState('');
	// Once the user edits the set name it stops tracking the task name.
	const [setNameEdited, setSetNameEdited] = useState(false);
	const [form, setForm] = useState<TaskSetFormState>(DEFAULT_FORM);
	// Drives the "Show all dates" expander. Collapsed by default: the range plus
	// the totals is the confirmation, the full list is on request.
	const [datesOpen, setDatesOpen] = useState(false);
	// Held as typed, converted once at the boundary. On an hours project the field
	// is hours and the column stores minutes, so it is multiplied; on a points
	// project the number IS the estimate and multiplying it by 60 would turn
	// 5 points into 300.
	const [estimateEntered, setEstimateEntered] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [roleId, setRoleId] = useState<string | null>(null);
	const [assignedPersonId, setAssignedPersonId] = useState<string | null>(null);
	const [deliverableId, setDeliverableId] = useState<string | null>(null);
	const [statusColumnId, setStatusColumnId] = useState<string | null>(null);
	const [description, setDescription] = useState('');
	// Collapsed by default. Role and assignee are out here because they carry the
	// financial story; deliverable, status and description are the ones a user can
	// reasonably leave alone, so they start out of the way.
	const [moreFieldsOpen, setMoreFieldsOpen] = useState(false);

	// A typo must not become a number. Number('2x') is NaN, and NaN reached the
	// preview as "NaNh" and made the ceiling check refuse a legal set.
	const estimateParsed = ((): number | null => {
		if (!estimateEntered.trim()) {
			return null;
		}
		const parsed = Number(estimateEntered);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
	})();
	const estimateForecast =
		estimateParsed === null ? null : isPointsProject ? estimateParsed : Math.round(estimateParsed * 60);

	const preview = useTaskSetPreview(form);

	// The rate quoted beside the role picker: the one in force on the first
	// instance, since that is the date the reader can see. Null when no role is
	// chosen or the card prices nothing on that date, so the hint is absent rather
	// than inventing a number -- and absent is distinct from a zero, which is a
	// real rate.
	const firstInstanceDate = preview.dates[0] ?? form.seedDate;
	const selectedRoleRate =
		roleId && rateForRoleOnDate && firstInstanceDate ? rateForRoleOnDate(roleId, firstInstanceDate) : null;

	// Priced per instance rather than by one rate times the count, because the
	// rate can change part-way through a set: a card whose rate rises next month
	// bills the instances before and after it differently. Summed over the dates
	// the preview will actually create, so an over-ceiling rule -- which is
	// refused anyway -- cannot inflate the figure.
	//
	// Hours only. A points estimate has no hours to price, so there is no revenue
	// to state rather than a zero that reads as "free".
	const planRevenue = (() => {
		if (isPointsProject || !estimateForecast || !roleId || !rateForRoleOnDate) {
			return null;
		}
		const hoursPerInstance = estimateForecast / 60;
		let total = 0;
		let priced = false;
		for (const date of preview.dates) {
			const rate = rateForRoleOnDate(roleId, date);
			if (rate !== null) {
				total += hoursPerInstance * rate;
				priced = true;
			}
		}
		return priced ? total : null;
	})();

	const namePrefix = formatMessage({id: 'task_set.name_prefix'});
	const effectiveSetName = setNameEdited ? setName : defaultSetName(instanceName, namePrefix);

	const onInstanceNameChange = (value: string) => {
		setInstanceName(value);
		if (!setNameEdited) {
			setSetName(defaultSetName(value, namePrefix));
		}
	};

	// Fires on the transition into the over-ceiling state, not on every render.
	// Without the ref this reports once per keystroke and the numbers are useless.
	const reportedOverCeiling = useRef(false);
	useEffect(() => {
		if (preview.overCeiling && !reportedOverCeiling.current) {
			reportedOverCeiling.current = true;
			// A weekday count rather than the days themselves: the question this has
			// to answer is whether every-weekday patterns are what hit the wall.
			trackEvent('Task Set', 'Blocked By Instance Ceiling', {
				requestedCount: preview.count,
				ceiling: MAX_OCCURRENCES,
				frequency: form.frequency,
				intervalLength: form.intervalLength,
				weekdayCount: form.weekdays.length,
				boundType: form.boundType,
			});
		}
		if (!preview.overCeiling) {
			reportedOverCeiling.current = false;
		}
	}, [preview.overCeiling, preview.count, form]);

	const weekdaysMissing = form.frequency === 'WEEKLY' && form.weekdays.length === 0;
	const canCreate =
		!submitting &&
		Boolean(instanceName) &&
		Boolean(effectiveSetName) &&
		Boolean(form.seedDate) &&
		!weekdaysMissing &&
		preview.count > 0 &&
		!preview.overCeiling;

	const onCreate = () => {
		setSubmitting(true);
		Util.CommitMutation(
			CreateTaskSetMutation,
			{
				globalProjectId: projectId,
				globalPhaseId: phaseId ?? null,
				frequency: form.frequency,
				intervalLength: form.intervalLength,
				weekdays: form.weekdays,
				monthlyWeekday: form.monthlyWeekday,
				monthlyMode: form.monthlyMode,
				dayOfMonth: form.dayOfMonth,
				weekOrdinal: form.weekOrdinal,
				seedDate: form.seedDate,
				boundType: form.boundType,
				boundCount: form.boundCount,
				boundEndDate: form.boundEndDate,
				setName: effectiveSetName,
				instanceName,
				estimateForecast: estimateForecast,
				globalRoleId: roleId,
				globalAssignedPersonId: assignedPersonId,
				globalDeliverableId: deliverableId,
				globalStatusColumnId: statusColumnId,
				description: description.trim() || null,
			},
			() => {
				setSubmitting(false);
				trackEvent('Task Set', 'Created', {frequency: form.frequency, instances: preview.count});
				onCreated?.();
				onClose();
			},
			true,
			null,
			// Without this the drawer stays submitting for ever and Create is dead,
			// with no way out but Escape. The fallback keeps the standard error modal.
			//
			// The fallback returns true when it has scheduled its own retry, meaning
			// this attempt is not over. Re-enabling Create then would let a second
			// create fire while the first is still in flight, so the form stays
			// submitting until an attempt actually finishes.
			(transaction: unknown, onFailure?: (t: unknown) => boolean | void) => {
				const retryInFlight = onFailure ? onFailure(transaction) === true : false;
				if (!retryInFlight) {
					setSubmitting(false);
				}
			}
		);
	};

	const subtitle = [projectName, phaseName].filter(Boolean).join(' · ') || undefined;

	const previewMessage = preview.overCeiling
		? formatMessage(
				{
					id: preview.countIsAtProbeLimit ? 'task_set.too_many_instances_unbounded' : 'task_set.too_many_instances',
				},
				{count: preview.count, max: MAX_OCCURRENCES}
		  )
		: formatMessage({id: 'task_set.preview_count'}, {count: preview.count});

	return (
		<TaskSetDrawer
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage({id: 'task_set.title'})}
			subtitle={subtitle}
			footer={
				<>
					<Button emphasis={EMPHASIS.MEDIUM} onPress={onClose} data-cy="task-set-cancel">
						{formatMessage({id: 'common.cancel'})}
					</Button>
					{/* Creation makes one task per instance, so a bounded set of twenty-six is
					    a visibly long wait. waiting is the design system's own affordance:
					    it swaps the label for a loader and disables the button, so the only
					    feedback is no longer the label going grey. */}
					<Button
						emphasis={EMPHASIS.HIGH}
						disabled={!canCreate}
						waiting={submitting}
						onPress={onCreate}
						data-cy="task-set-create"
					>
						{formatMessage({id: 'task_set.create'})}
					</Button>
				</>
			}
		>
			<Row>
				<TextField
					label={formatMessage({id: 'task_set.instance_name'})}
					value={instanceName}
					onValueChange={onInstanceNameChange}
					width="flex"
				/>
				<TextField
					label={formatMessage({id: 'task_set.set_name'})}
					value={effectiveSetName}
					onValueChange={(value: string) => {
						setSetNameEdited(true);
						setSetName(value);
					}}
					width="flex"
				/>
			</Row>
			<Hint>{formatMessage({id: 'task_set.set_name_help'})}</Hint>

			<Row>
				<DatePicker
					label={formatMessage({id: 'task_set.first_instance'})}
					value={form.seedDate}
					onChange={(value: string) =>
						// The pattern's day is derived from this date, so it moves when the
						// date does. Without this, picking monthly on the 15th and then
						// changing the date to the 3rd would keep generating on the 15th.
						setForm({...form, ...patternDefaults(form.frequency, value, form.weekdays), seedDate: value})
					}
				/>
				<TextField
					label={formatMessage({id: 'task_set.estimate'})}
					value={estimateEntered}
					onValueChange={setEstimateEntered}
					width="flex"
				/>
			</Row>
			{/* Only true on an hours project. A points estimate does not drive the
			    deadline -- a points instance keeps deadline = start -- so claiming it
			    does would be worse than saying nothing. */}
			{isPointsProject ? null : <Hint larger>{formatMessage({id: 'task_set.estimate_help'})}</Hint>}

			<Row>
				<OptionalPicker
					label={formatMessage({id: 'task_set.role_optional'})}
					noneLabel={formatMessage({id: 'common.none'})}
					items={roles.filter(r => r.name).map(r => ({id: r.id, name: r.name as string}))}
					value={roleId}
					onChange={setRoleId}
					dataCy="task-set-role"
				/>
				<OptionalPicker
					label={formatMessage({id: 'task_set.assignee_optional'})}
					noneLabel={formatMessage({id: 'task_set.unassigned'})}
					items={assignablePersons.map(p => ({
						id: p.id,
						name: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.id,
					}))}
					value={assignedPersonId}
					onChange={setAssignedPersonId}
					dataCy="task-set-assignee"
				/>
			</Row>
			{/* The rate is what makes the plan-revenue figure below legible: without it
			    the number appears from nowhere. Only shown once a role is chosen and
			    the rate card actually prices it. */}
			{selectedRoleRate === null ? null : (
				<Hint>{formatMessage({id: 'task_set.role_rate'}, {rate: `${currencySymbol}${selectedRoleRate}`})}</Hint>
			)}

			<MoreFieldsToggle
				type="button"
				onClick={() => setMoreFieldsOpen(!moreFieldsOpen)}
				aria-expanded={moreFieldsOpen}
				data-cy="task-set-more-fields"
			>
				{formatMessage({id: 'task_set.more_fields'})}
				<MoreFieldsHint>{formatMessage({id: 'task_set.more_fields_hint'})}</MoreFieldsHint>
			</MoreFieldsToggle>
			{moreFieldsOpen ? (
				<>
					<Row>
						<OptionalPicker
							label={formatMessage({id: 'common.deliverable'})}
							noneLabel={formatMessage({id: 'task_set.no_deliverable'})}
							items={deliverables.filter(d => d.name).map(d => ({id: d.id, name: d.name as string}))}
							value={deliverableId}
							onChange={setDeliverableId}
							dataCy="task-set-deliverable"
						/>
						<OptionalPicker
							label={formatMessage({id: 'common.status'})}
							noneLabel={formatMessage({id: 'common.none'})}
							items={statusColumns.filter(c => c.name).map(c => ({id: c.id, name: c.name as string}))}
							value={statusColumnId}
							onChange={setStatusColumnId}
							dataCy="task-set-status"
						/>
					</Row>
					<Row>
						<TextField
							label={formatMessage({id: 'common.description'})}
							value={description}
							onValueChange={setDescription}
							width="flex"
							data-cy="task-set-description"
						/>
					</Row>
				</>
			) : null}

			<Row>
				<Field>
					<Label>{formatMessage({id: 'task_set.pattern'})}</Label>
					<ComboBox
						aria-label={formatMessage({id: 'task_set.pattern'})}
						defaultItems={(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const).map(value => ({
							id: value,
							name: formatMessage({id: `task_set.frequency_${value.toLowerCase()}`}),
						}))}
						value={form.frequency}
						onChange={(value: TaskSetFormState['frequency']) =>
							setForm({...form, ...patternDefaults(value, form.seedDate), frequency: value})
						}
						width="flex"
					>
						{item => (
							<ComboBox.Item textValue={item.name}>
								<Text noWrap>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</Field>
			</Row>

			{form.frequency === 'WEEKLY' ? (
				<Field>
					<Label>{formatMessage({id: 'task_set.weekdays'})}</Label>
					{/* Seven chips rather than a select: a weekly rule carries a SET of
					    days, and this is the control the existing repeat modal uses.
					    Reuses the short weekday keys, which are "Mon", "Tue" and so on
					    -- unambiguous, unlike single letters. */}
					<ToggleGroup
						type="multiple"
						value={form.weekdays}
						onValueChange={(value: string | string[]) =>
							setForm({...form, weekdays: Array.isArray(value) ? value : [value]})
						}
						options={WEEKDAYS.map(day => ({
							value: day,
							label: formatMessage({id: `common.${day.toLowerCase()}.short`}),
							disabled: false,
						}))}
					/>
					{weekdaysMissing ? <Hint>{formatMessage({id: 'task_set.weekdays_required'})}</Hint> : null}
				</Field>
			) : null}

			<Row>
				<Field>
					<Label>{formatMessage({id: 'task_set.ends'})}</Label>
					<ComboBox
						aria-label={formatMessage({id: 'task_set.ends'})}
						defaultItems={[
							{id: 'COUNT', name: formatMessage({id: 'task_set.ends_after'})},
							{id: 'DATE', name: formatMessage({id: 'task_set.ends_on_date'})},
						]}
						value={form.boundType}
						onChange={(value: TaskSetFormState['boundType']) => setForm({...form, boundType: value})}
						width="flex"
					>
						{item => (
							<ComboBox.Item textValue={item.name}>
								<Text noWrap>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</Field>
				{form.boundType === 'COUNT' ? (
					<TextField
						label={formatMessage({id: 'task_set.ends_occurrences'})}
						value={String(form.boundCount ?? '')}
						onValueChange={(value: string) => setForm({...form, boundCount: countFrom(value, form.boundCount)})}
						width="flex"
					/>
				) : (
					<DatePicker
						label={formatMessage({id: 'task_set.ends_on_date'})}
						value={form.boundEndDate ?? ''}
						onChange={(value: string) => setForm({...form, boundEndDate: value})}
					/>
				)}
			</Row>

			<Callout>{formatMessage({id: 'task_set.in_advance_help'})}</Callout>

			<Preview>
				<Text>{previewMessage}</Text>
				{preview.dates.length > 0 ? (
					<>
						<Text>
							{formatMessage(
								{id: 'task_set.preview_range'},
								{
									first: formatSetDate(intl, preview.dates[0]) ?? preview.dates[0],
									last:
										formatSetDate(intl, preview.dates[preview.dates.length - 1]) ??
										preview.dates[preview.dates.length - 1],
								}
							)}
						</Text>
						<Button emphasis={EMPHASIS.LOW} onPress={() => setDatesOpen(!datesOpen)}>
							{formatMessage({id: datesOpen ? 'task_set.hide_dates' : 'task_set.show_all_dates'})}
						</Button>
						{datesOpen
							? preview.dates.map(date => <Text key={date}>{formatSetDate(intl, date) ?? date}</Text>)
							: null}
						<PreviewStats>
							<Stat>
								<span>{formatMessage({id: 'task_set.instances'})}</span>
								<b>{preview.count}</b>
							</Stat>
							{estimateForecast ? (
								<Stat>
									<span>{formatMessage({id: 'task_set.total_estimate'})}</span>
									{/* estimateForecast is minutes on an hours project and points on a
									    points project, so the total is divided and suffixed to match
									    rather than always reading as hours. */}
									<b>
										{isPointsProject
											? `${preview.count * estimateForecast}${formatMessage({id: 'common.points.short'})}`
											: `${(preview.count * estimateForecast) / 60}${formatMessage({
													id: 'common.hours.short',
											  })}`}
									</b>
								</Stat>
							) : null}
							{planRevenue === null ? null : (
								<Stat data-cy="task-set-plan-revenue">
									<span>{formatMessage({id: 'task_set.plan_revenue'})}</span>
									<b>{`${currencySymbol}${Math.round(planRevenue)}`}</b>
								</Stat>
							)}
						</PreviewStats>
					</>
				) : null}
			</Preview>
		</TaskSetDrawer>
	);
};
